import React, { Fragment, useCallback, useMemo } from 'react';

import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Paper,
  ToggleButton,
} from '@mui/material';
import { Form, useForm } from 'react-final-form';
import { TextField } from 'mui-rff';
import _mergeWith from 'lodash/mergeWith';

import Title from 'components/common/Title';
import ButtonSubmit from 'components/common/ButtonSubmit';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { mergeArrayOverwriteCustomizer } from 'utils/common';
import { useSnackbar } from 'notistack';
import { useBoolFlag } from 'hooks/index';
import { styled } from '@mui/material/styles';
import {
  useGetWLIDByIDQuery,
  useUpdateWLIDSettingsMutation,
} from '../../../../store/api/wl.api';

const DATA_CENTERS = ['US', 'EU'];
const DOMAIN_TYPES = [
  ['track', 'Track'],
  ['pixel', 'Pixel'],
  ['rtb', 'RTB'],
  ['direct', 'Direct'],
  ['ads', 'Ads'],
];
const identity = (value) => value;

// wrapper for RFF TextField that overrides identity check;
// created to prevent cases, when string was set to '', and not submitted
// see https://github.com/final-form/react-final-form/issues/430
// see https://github.com/final-form/react-final-form/issues/130 (fix picked from here)
function TextFieldIdentity({ fieldProps = {}, ...props }) {
  return (
    <TextField fieldProps={{ parse: identity, ...fieldProps }} {...props} />
  );
}
const StyledTitle = styled(Title)(() => ({
  margin: 0,
}));
function ExpandableTextArea({ defaultRows = 3, disabled, ...fieldProps }) {
  const { isOn: expanded, toggle } = useBoolFlag();

  return (
    <Box display='flex' style={{ gap: '16px' }}>
      <TextFieldIdentity
        multiline
        variant='outlined'
        minRows={defaultRows}
        maxRows={expanded ? undefined : defaultRows}
        autoComplete='off'
        autoCorrect='off'
        autoCapitalize='off'
        spellCheck={false}
        disabled={disabled}
        {...fieldProps}
      />
      <div>
        <ToggleButton value='expand' selected={expanded} onChange={toggle}>
          <AspectRatioIcon />
        </ToggleButton>
      </div>
    </Box>
  );
}

function EntryDetails({ label, prefix, disabled }) {
  const {
    mutators: { push },
  } = useForm();
  const onItemAdd = useCallback(() => {
    push(`${prefix}.ip`, '');
  }, [prefix, push]);

  return (
    <Fragment>
      <Grid container alignItems='flex-start' spacing={2}>
        <Grid size={{ xs: 3 }}>
          <TextFieldIdentity
            disabled={disabled}
            label={label + ' Domain'}
            name={prefix + '.domain'}
          />
        </Grid>
        <Grid size={{ xs: 3 }}>
          <TextFieldIdentity
            disabled={disabled}
            label={label + ' CNAME'}
            name={prefix + '.cname'}
          />
        </Grid>
        <FieldArray name={prefix + '.ip'}>
          {({ fields }) =>
            fields.map((name, idx) => (
              <Grid key={name} size={{ xs: 3 }}>
                <Box display='flex' key={name}>
                  <TextFieldIdentity
                    disabled={disabled}
                    label={label + ' IP #' + (idx + 1)}
                    name={name}
                  />
                  <IconButton
                    onClick={() => fields.remove(idx)}
                    disabled={fields.length <= 1 || disabled}
                    size='small'
                    color='primary'
                    aria-label='copy'
                  >
                    <DeleteForeverOutlinedIcon fontSize='small' />
                  </IconButton>
                </Box>
              </Grid>
            ))
          }
        </FieldArray>
        <Grid size={{ xs: 3 }}>
          <Button
            variant='contained'
            color='primary'
            size='small'
            style={{ marginTop: 8 }}
            fullWidth
            disabled={disabled}
            onClick={onItemAdd}
          >
            {`Add new ${label} IP`}
          </Button>
        </Grid>
      </Grid>
    </Fragment>
  );
}

function DataCenterDetails({ dataCenter, disabled }) {
  return (
    <Fragment>
      <StyledTitle>{dataCenter} Data Center</StyledTitle>
      {DOMAIN_TYPES.map(([type, label]) => (
        <EntryDetails
          disabled={disabled}
          key={type}
          prefix={`dataCenters.${dataCenter}.${type}`}
          label={label}
        />
      ))}
    </Fragment>
  );
}

const INITIAL = {
  admin: { panel: { ip: [''] }, publicApi: { ip: [''] } },
  dataCenters: Object.fromEntries(
    DATA_CENTERS.map((center) => [
      center,
      Object.fromEntries(DOMAIN_TYPES.map(([type]) => [type, { ip: [''] }])),
    ]),
  ),
};

export default function Settings({ id, onFormStateChanged, editable }) {
  const { enqueueSnackbar } = useSnackbar();
  const { data } = useGetWLIDByIDQuery(id, {
    skip: !id,
  });

  const settings = useMemo(
    () => _mergeWith({}, INITIAL, data.settings, mergeArrayOverwriteCustomizer),
    [data],
  );
  const [mutate] = useUpdateWLIDSettingsMutation();

  const submitData = useCallback(
    async (data) => {
      try {
        console.log('sub', { data });
        await mutate({ id, ...data }).unwrap();
        enqueueSnackbar('Settings updated successfully', {
          variant: 'success',
        });
      } catch (e) {
        enqueueSnackbar('Failed to update settings', { variant: 'error' });
      }
    },
    [mutate, id, enqueueSnackbar],
  );

  const decorators = useMemo(
    () => [
      ({ subscribe }) =>
        subscribe(({ dirty }) => onFormStateChanged(dirty), { dirty: true }),
    ],
    [onFormStateChanged],
  );

  return (
    <React.Fragment>
      <Paper style={{ padding: 16 }}>
        <Form
          initialValues={settings}
          mutators={arrayMutators}
          decorators={decorators}
          onSubmit={submitData}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <Box display='flex' flexDirection='column' gap='16px'>
                <StyledTitle>Admin Panel</StyledTitle>
                <Grid container alignItems='flex-start' spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <EntryDetails
                      disabled={!editable}
                      label='Admin Panel'
                      prefix='admin.panel'
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <EntryDetails
                      disabled={!editable}
                      label='Public API'
                      prefix='admin.publicApi'
                    />
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <TextFieldIdentity
                      disabled={!editable}
                      label='REST API Auth Key'
                      name='admin.apiKey'
                    />
                  </Grid>
                </Grid>
                <Divider />
                {DATA_CENTERS.map((value) => (
                  <React.Fragment key={value}>
                    <DataCenterDetails
                      dataCenter={value}
                      disabled={!editable}
                    />
                    <Divider />
                  </React.Fragment>
                ))}
                <Title>SSL</Title>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <ExpandableTextArea
                      disabled={!editable}
                      label='RTB/Track Domain SSL'
                      name='admin.domainSSL'
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <ExpandableTextArea
                      disabled={!editable}
                      label='RTB/Track Domain Private Key'
                      name='admin.domainPrivateKey'
                    />
                  </Grid>
                </Grid>
                <Divider />

                <ButtonSubmit
                  name='save'
                  disabled={!editable}
                  isPending={submitting}
                  onSubmit={handleSubmit}
                  type='submit'
                />
              </Box>
            </form>
          )}
        />
      </Paper>
    </React.Fragment>
  );
}
